/**
 * guidance-nudge-storage — Persistance localStorage des « coups de pouce ».
 *
 * Effets isolés (lecture/écriture navigateur) séparés de la logique pure
 * (`guidance-nudge-select.ts`). Convention de clés `s1_*` du projet :
 *  - `s1_nudges_enabled`         → interrupteur global ("0"/"1"), défaut = activé.
 *  - `s1_nudge_dismissed_<key>`  → « Ne plus voir » (masquage définitif).
 *  - `s1_nudge_snooze_<key>`     → « Fermer » : reporté jusqu'à cet horodatage (ms).
 *  - `s1_nudge_last_at`          → dernier conseil affiché (ms), pour le cooldown ambiant.
 *  - `s1_nudge_shown_session`    → (sessionStorage) liste JSON des clés déjà montrées
 *                                   cette session (chaque conseil au plus 1 fois/session).
 *
 * `cache-utils.clearNudgeCache()` efface tout le préfixe `s1_nudge` au logout.
 * Tout est défensif (navigation privée / quota) : aucune exception ne remonte.
 */

const ENABLED_KEY = "s1_nudges_enabled";
const DISMISSED_PREFIX = "s1_nudge_dismissed_";
const SNOOZE_PREFIX = "s1_nudge_snooze_";
const LAST_AT_KEY = "s1_nudge_last_at";
const SHOWN_SESSION_KEY = "s1_nudge_shown_session";

// ─── Interrupteur global ──────────────────────────────────────────────────────

/** Conseils activés ? Défaut = true (clé absente = on accompagne le nouvel utilisateur). */
export function isNudgesEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== "0";
  } catch {
    return true;
  }
}

export function setNudgesEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "1" : "0");
  } catch {
    /* navigation privée / quota — silencieux */
  }
}

// ─── « Ne plus voir » (définitif) ─────────────────────────────────────────────

export function getDismissedKeys(): string[] {
  return collectKeys(DISMISSED_PREFIX);
}

export function dismissForever(key: string): void {
  try {
    localStorage.setItem(`${DISMISSED_PREFIX}${key}`, "1");
  } catch {
    /* silencieux */
  }
}

// ─── « Fermer » (report / snooze) ─────────────────────────────────────────────

export function getSnoozedUntil(): Record<string, number> {
  const out: Record<string, number> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (!fullKey?.startsWith(SNOOZE_PREFIX)) continue;
      const value = Number(localStorage.getItem(fullKey));
      if (Number.isFinite(value)) {
        out[fullKey.slice(SNOOZE_PREFIX.length)] = value;
      }
    }
  } catch {
    /* silencieux */
  }
  return out;
}

export function snoozeNudge(key: string, untilMs: number): void {
  try {
    localStorage.setItem(`${SNOOZE_PREFIX}${key}`, String(untilMs));
  } catch {
    /* silencieux */
  }
}

// ─── Cooldown ambiant ─────────────────────────────────────────────────────────

export function getLastNudgeAt(): number | null {
  try {
    const raw = localStorage.getItem(LAST_AT_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

export function setLastNudgeAt(ms: number): void {
  try {
    localStorage.setItem(LAST_AT_KEY, String(ms));
  } catch {
    /* silencieux */
  }
}

// ─── « Au plus 1 fois par session » (par clé) ─────────────────────────────────

export function getShownKeysThisSession(): string[] {
  try {
    const raw = sessionStorage.getItem(SHOWN_SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}

export function markShownThisSession(key: string): void {
  try {
    const current = getShownKeysThisSession();
    if (current.includes(key)) return;
    sessionStorage.setItem(SHOWN_SESSION_KEY, JSON.stringify([...current, key]));
  } catch {
    /* silencieux */
  }
}

// ─── Util interne ─────────────────────────────────────────────────────────────

function collectKeys(prefix: string): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (fullKey?.startsWith(prefix)) {
        out.push(fullKey.slice(prefix.length));
      }
    }
  } catch {
    /* silencieux */
  }
  return out;
}
